import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  AdminSecurities, 
  AdminSecuritiesCreateDto, 
  AdminSecuritiesUpdateDto,
  AdminSecuritiesResponse,
  AdminSecuritiesLatestResponse
} from "../../modules/shared/model";
import { 
  getLatestAdminSecurities as getLatestAdminSecuritiesAction,
  getAdminSecurities as getAdminSecuritiesAction, 
  getAdminSecuritiesById as getAdminSecuritiesByIdAction,
  createAdminSecuritiesAction,
  updateAdminSecuritiesAction,
  deleteAdminSecuritiesAction
} from "../Actions/AdminSecuritiesActions";

// Async Thunks
export const fetchLatestAdminSecurities = createAsyncThunk("adminSecurities/fetchLatest", async (_, { rejectWithValue }) => {
  try { 
    return await getLatestAdminSecuritiesAction(); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch latest admin securities"); 
  }
});

export const fetchAdminSecurities = createAsyncThunk("adminSecurities/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getAdminSecuritiesAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch admin securities"); 
  }
});

export const fetchAdminSecuritiesById = createAsyncThunk("adminSecurities/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getAdminSecuritiesByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch admin securities"); 
  }
});

export const createAdminSecurities = createAsyncThunk("adminSecurities/create", async (data: AdminSecuritiesCreateDto, { rejectWithValue }) => {
  try { 
    return await createAdminSecuritiesAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create admin securities"); 
  }
});

export const updateAdminSecurities = createAsyncThunk("adminSecurities/update", async ({ id, data }: { id: string; data: AdminSecuritiesUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateAdminSecuritiesAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update admin securities"); 
  }
});

export const deleteAdminSecurities = createAsyncThunk("adminSecurities/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteAdminSecuritiesAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete admin securities"); 
  }
});

// State interface
interface AdminSecuritiesState {
  // Latest settings (single item)
  latestSettings: AdminSecurities | null;
  latestSettingsLoading: boolean;
  latestSettingsError: string | null;
  
  // All settings (paginated)
  settings: AdminSecurities[];
  currentSettings: AdminSecurities | null;
  settingsLoading: boolean;
  settingsError: string | null;
  settingsTotalCount?: number;
  settingsCurrentPage: number;
  settingsHasNextPage: boolean;
  settingsHasPreviousPage: boolean;
  settingsSearchQuery: string;
  
  // Form state
  isFormDirty: boolean;
  lastSavedAt: string | null;
}

const initialState: AdminSecuritiesState = {
  // Latest settings
  latestSettings: null,
  latestSettingsLoading: false,
  latestSettingsError: null,
  
  // All settings
  settings: [],
  currentSettings: null,
  settingsLoading: false,
  settingsError: null,
  settingsTotalCount: 0,
  settingsCurrentPage: 1,
  settingsHasNextPage: false,
  settingsHasPreviousPage: false,
  settingsSearchQuery: "",
  
  // Form state
  isFormDirty: false,
  lastSavedAt: null,
};

const adminSecuritiesSlice = createSlice({
  name: "adminSecurities",
  initialState,
  reducers: {
    clearLatestSettingsError(state) { 
      state.latestSettingsError = null; 
    },
    clearSettingsError(state) { 
      state.settingsError = null; 
    },
    clearCurrentSettings(state) { 
      state.currentSettings = null; 
    },
    setSettingsSearchQuery(state, action: PayloadAction<string>) { 
      state.settingsSearchQuery = action.payload; 
    },
    setAdminSecuritiesFormDirty(state, action: PayloadAction<boolean>) { 
      state.isFormDirty = action.payload; 
    },
    clearAllSettings(state) {
      state.settings = [];
      state.currentSettings = null;
      state.latestSettings = null;
      state.settingsTotalCount = 0;
    },
    updateLatestSettingsFromForm(state, action: PayloadAction<Partial<AdminSecurities>>) {
      if (state.latestSettings) {
        state.latestSettings = { ...state.latestSettings, ...action.payload };
      }
    }
  },
  extraReducers: (builder) => {
    // Latest Settings Extra Reducers
    builder
      .addCase(fetchLatestAdminSecurities.pending, (state) => { 
        state.latestSettingsLoading = true; 
        state.latestSettingsError = null; 
      })
      .addCase(fetchLatestAdminSecurities.fulfilled, (state, action: PayloadAction<AdminSecuritiesLatestResponse>) => { 
        state.latestSettingsLoading = false; 
        state.latestSettings = action.payload; 
        state.lastSavedAt = new Date().toISOString();
      })
      .addCase(fetchLatestAdminSecurities.rejected, (state, action: any) => { 
        state.latestSettingsLoading = false; 
        state.latestSettingsError = action.payload as string; 
      });

    // All Settings Extra Reducers
    builder
      .addCase(fetchAdminSecurities.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(fetchAdminSecurities.fulfilled, (state, action: PayloadAction<AdminSecuritiesResponse>) => { 
        state.settingsLoading = false; 
        state.settings = action.payload.data || []; 
        state.settingsTotalCount = action.payload.totalCount || 0; 
        state.settingsCurrentPage = action.payload.page || 1;
        state.settingsHasNextPage = action.payload.hasNextPage || false;
        state.settingsHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchAdminSecurities.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    builder
      .addCase(fetchAdminSecuritiesById.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(fetchAdminSecuritiesById.fulfilled, (state, action: PayloadAction<AdminSecurities>) => { 
        state.settingsLoading = false; 
        state.currentSettings = action.payload; 
      })
      .addCase(fetchAdminSecuritiesById.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    // Create Extra Reducers
    builder
      .addCase(createAdminSecurities.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(createAdminSecurities.fulfilled, (state, action: PayloadAction<AdminSecurities>) => { 
        state.settingsLoading = false; 
        state.settings.unshift(action.payload); 
        state.settingsTotalCount = (state.settingsTotalCount || 0) + 1; 
        state.latestSettings = action.payload; // Update latest settings
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createAdminSecurities.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    // Update Extra Reducers
    builder
      .addCase(updateAdminSecurities.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(updateAdminSecurities.fulfilled, (state, action: PayloadAction<AdminSecurities>) => {
        state.settingsLoading = false;
        const index = state.settings.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.settings[index] = action.payload;
        if (state.currentSettings?.id === action.payload.id) state.currentSettings = action.payload;
        state.latestSettings = action.payload; // Update latest settings
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updateAdminSecurities.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    // Delete Extra Reducers
    builder
      .addCase(deleteAdminSecurities.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(deleteAdminSecurities.fulfilled, (state, action: PayloadAction<string>) => { 
        state.settingsLoading = false; 
        state.settings = state.settings.filter(x => x.id !== action.payload); 
        if (state.currentSettings?.id === action.payload) state.currentSettings = null; 
        if (state.latestSettings?.id === action.payload) state.latestSettings = null;
        state.settingsTotalCount = Math.max(0, (state.settingsTotalCount || 0) - 1); 
      })
      .addCase(deleteAdminSecurities.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });
  }
});

export const { 
  clearLatestSettingsError,
  clearSettingsError, 
  clearCurrentSettings, 
  setSettingsSearchQuery,
  setAdminSecuritiesFormDirty,
  clearAllSettings,
  updateLatestSettingsFromForm
} = adminSecuritiesSlice.actions;

export default adminSecuritiesSlice.reducer;
